"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiClient = exports.ApiError = void 0;
// Tarayıcı/Edge paketlerine Joi sızmasın diye kök barrel yerine alt yol kullanılır.
const errors_1 = require("@icebreaker/shared/errors");
class ApiError extends Error {
    body;
    constructor(body) {
        super(`API error ${body.code} (${body.statusCode})`);
        this.body = body;
        this.name = 'ApiError';
    }
}
exports.ApiError = ApiError;
class ApiClient {
    options;
    constructor(options) {
        this.options = options;
    }
    /**
     * Şemaya bağlı çağrı. Uç noktalar (`endpoints.ts`) YALNIZCA bunu kullanır.
     *
     *   client.call('/media/{id}', 'get', { path: { id } })
     */
    async call(path, method, options) {
        const resolved = interpolatePath(path, options.path);
        return this.request(resolved, {
            method,
            query: options.query,
            headers: definedHeaders(options.header),
            body: options.body,
            signal: options.signal,
            keepalive: options.keepalive,
        });
    }
    /** Alt seviye çağrı — şema dışı bir yola gitmek gerekirse (şu an kullanılmıyor). */
    async request(path, options = {}) {
        const fetchImpl = this.options.fetchImpl ?? globalThis.fetch;
        const url = new URL(this.options.baseUrl.replace(/\/$/, '') + path);
        for (const [key, value] of Object.entries(options.query ?? {})) {
            appendQueryValue(url.searchParams, key, value);
        }
        const headers = {
            ...options.headers,
            accept: 'application/json',
            'accept-language': this.options.getLocale?.() ?? 'tr',
        };
        const requestId = this.options.getRequestId?.();
        if (requestId)
            headers['x-request-id'] = requestId;
        const userAgent = this.options.getUserAgent?.();
        if (userAgent)
            headers['user-agent'] = userAgent;
        const token = await this.options.getAccessToken?.();
        if (token)
            headers.authorization = `Bearer ${token}`;
        if (options.body !== undefined)
            headers['content-type'] = 'application/json';
        if (options.responseType === 'text')
            headers.accept = 'text/plain, text/csv, text/vcard, */*';
        /*
         * ZAMAN AŞIMI: çağıranın kendi `signal`ı KORUNUR — ikisinden hangisi önce
         * düşerse istek iptal olur. `AbortSignal.any` her ortamda yok (RN Hermes),
         * o yüzden köprü elle kurulur.
         */
        const timeoutMs = this.options.timeoutMs;
        const controller = timeoutMs === undefined ? null : new AbortController();
        /*
         * Kesen ZAMAN AŞIMI mı, çağıranın iptali mi: ayrımı yalnız timer bilir
         * (sıra 66b). İkisi de aynı `AbortError`ı üretir; işaret olmadan ekran
         * "sunucu isteği işliyor olabilir" ile "istek hiç gitmedi"yi ayıramaz.
         */
        let timedOut = false;
        const timer = controller === null
            ? null
            : setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, timeoutMs);
        const onOuterAbort = () => controller?.abort();
        if (controller && options.signal) {
            if (options.signal.aborted)
                controller.abort();
            else
                options.signal.addEventListener('abort', onOuterAbort);
        }
        /*
         * TEMİZLİK GÖVDE OKUNDUKTAN SONRA. `fetch` yalnız BAŞLIKLARLA çözülür;
         * gövde ayrı bir akıştır ve sunucu yazmayı bırakırsa `text()`/`json()`
         * SÜRESİZ asılı kalabilir. Timer bu yüzden en dışta, tek `finally`de
         * temizlenir — zaman aşımı gövdeyi de kapsar.
         */
        try {
            const response = await fetchImpl(url.toString(), {
                method: (options.method ?? 'get').toUpperCase(),
                headers,
                body: options.body === undefined ? undefined : JSON.stringify(options.body),
                signal: controller?.signal ?? options.signal,
                credentials: this.options.credentials,
                // RN fetch'i bilinmeyen alanı yok sayar; bayrak yalnız istendiğinde eklenir.
                ...(options.keepalive ? { keepalive: true } : {}),
            });
            if (response.status === 204)
                return undefined;
            if (options.responseType === 'text') {
                const text = await response.text().catch(rethrowAbort(''));
                if (!response.ok) {
                    // Hata gövdesi JSON'dur; metin uçta da AYNI ret sözleşmesi geçerlidir.
                    let parsed = null;
                    try {
                        parsed = JSON.parse(text);
                    }
                    catch {
                        parsed = null;
                    }
                    throw new ApiError(withRetryAfter(toApiError(parsed, response.status), response));
                }
                return text;
            }
            const payload = await response.json().catch(rethrowAbort(null));
            if (!response.ok) {
                throw new ApiError(withRetryAfter(toApiError(payload, response.status), response));
            }
            return payload;
        }
        catch (error) {
            if (timedOut && timeoutMs !== undefined && isAbort(error)) {
                throw new errors_1.RequestTimeoutError(timeoutMs);
            }
            throw error;
        }
        finally {
            if (timer !== null)
                clearTimeout(timer);
            if (controller && options.signal)
                options.signal.removeEventListener('abort', onOuterAbort);
        }
    }
}
exports.ApiClient = ApiClient;
/**
 * Gövde okuma hatasını YUTAR — İPTAL HARİÇ.
 *
 * Boş ya da bozuk gövde normaldir (204 sonrası artık, HTML hata sayfası) ve
 * varsayılana düşer. Ama gövde okuması ZAMAN AŞIMIYLA iptal edildiyse yutmak
 * `null`/`''` ile SESSİZ BİR BAŞARI üretirdi: çağıran geçerli bir yanıt aldığını
 * sanır, oturum kararı boş veriyle verilirdi. İptal yukarı taşınır ve çağıran
 * normal hata yolundan geçer (tech-lead Blok 23 · Düşük).
 */
function rethrowAbort(fallback) {
    return (error) => {
        if (error instanceof Error && error.name === 'AbortError')
            throw error;
        return fallback;
    };
}
/** RN'in iptal hatası `DOMException` olabilir; ad üzerinden tanınır. */
function isAbort(error) {
    return (typeof error === 'object' &&
        error !== null &&
        error.name === 'AbortError');
}
/** Tanımsız başlık gönderilmez; değer dizgeye çevrilir. */
function definedHeaders(header) {
    if (header === undefined)
        return undefined;
    const out = {};
    for (const [name, value] of Object.entries(header)) {
        if (value !== undefined && value !== null)
            out[name] = String(value);
    }
    return out;
}
/**
 * Dizi sorgu parametresi TEKRARLI ANAHTAR olarak serileştirilir
 * (`?tagIds=a&tagIds=b`) — OpenAPI `style=form, explode=true` karşılığı.
 *
 * NEDEN: sunucu Express 5 üzerinde "simple" sorgu ayrıştırıcısı kullanır;
 * tekrarlı anahtar HER İKİ ayrıştırıcıda da dizi üretir, köşeli parantezli
 * (`tagIds[]=a`) biçim ise yalnız "extended"da çalışır. Eski davranış
 * (`String(value)`) diziyi virgüllü TEK dizgeye çeviriyordu ve sunucudaki
 * Joi `array()` şeması 400 döndürüyordu (Blok 8-4 bulgusu).
 *
 * Boş dizi HİÇ parametre üretmez: "filtre yok" ile "hiçbir şeyle eşleşme"
 * karışmasın (sunucu tarafında boş `IN ()` üretmek de mümkün değil).
 */
function appendQueryValue(params, key, value) {
    if (value === undefined)
        return;
    if (Array.isArray(value)) {
        for (const item of value)
            params.append(key, String(item));
        return;
    }
    params.set(key, String(value));
}
/** `/media/{id}` + `{ id: 'x' }` → `/media/x` */
function interpolatePath(path, params) {
    if (!params)
        return path;
    return path.replace(/\{(\w+)\}/g, (_match, key) => {
        const value = params[key];
        if (value === undefined)
            throw new Error(`Missing path parameter: ${key}`);
        return encodeURIComponent(String(value));
    });
}
function toApiError(payload, statusCode) {
    if (typeof payload === 'object' &&
        payload !== null &&
        'code' in payload &&
        typeof payload.code === 'string') {
        return payload;
    }
    return { code: errors_1.ERROR_CODES.INTERNAL_ERROR, statusCode, requestId: '' };
}
/**
 * `Retry-After` BAŞLIĞI GÖVDEYE TAŞINIR (Blok B · hız sınırı ekseni).
 *
 * Sunucu 429'da bekleme süresini yalnız başlıkta verir (`all-exceptions.filter`);
 * ekranın "kaç saniye sonra" yazabilmesi için değer hata gövdesine iliştirilir.
 * Başlık YOKSA ya da sayı değilse alan hiç yazılmaz: istemci tahmin üretmez,
 * ekran sözleşmedeki kova penceresine düşer. Yalnız 429'da bakılır — başka
 * durumlarda başlığın anlamı farklıdır (503 gibi) ve bu alan onları kapsamaz.
 */
function withRetryAfter(body, response) {
    if (response.status !== 429)
        return body;
    const header = response.headers?.get?.('retry-after');
    const seconds = header === null || header === undefined ? Number.NaN : Number(header);
    if (!Number.isFinite(seconds) || seconds < 0)
        return body;
    const retryAfterSeconds = Math.ceil(seconds);
    // Mutlak bitiş anı (sıra 64b): ekrandan çıkıp dönünce sayaç baştan başlamaz.
    return { ...body, retryAfterSeconds, retryUntil: Date.now() + retryAfterSeconds * 1000 };
}
//# sourceMappingURL=http-client.js.map